import { useEffect, useState } from 'react';
import {
  IonAlert,
  IonBadge,
  IonButton,
  IonIcon,
  IonSpinner,
} from '@ionic/react';
import { shareOutline, copyOutline, playOutline, stopOutline, bookmarkOutline, notificationsOutline } from 'ionicons/icons';
import { Card, Page } from '../components/Shell';
import { MapPicker } from '../components/MapPicker';
import { RideMap } from '../components/RideMap';
import { api } from '../lib/api';
import { enablePush, pushSupport } from '../lib/push';
import { isNative } from '../lib/tracking';
import { joinLink, shareText } from '../lib/share';
import { saveRide } from '../lib/savedrides';
import { STATUS_COLOR, STATUS_LABEL, clockTime, isPositionFresh, timelineLabel } from '../lib/status';
import { formatAge, formatDistance, formatDuration } from '@shared/format.js';
import type { Connection, Coords, Ride } from '../lib/types';
import type { Session } from '../lib/session';

interface Props {
  session: Session;
  ride: Ride;
  /** Gesetzt, wenn die Fahrt über einen Kontakt lief - fürs Speichern. */
  contact?: Connection | null;
  /** Nur gesetzt, wenn es weitere laufende Fahrten gibt - Wegnavigieren
   *  beendet die Übertragung nicht mehr, ein Zurück-Weg ist also unbedenklich. */
  onBack?: () => void;
  /** true, wenn gerade DIESE Fahrt die (immer nur eine) Standortübertragung hält. */
  isTracking: boolean;
  /** true, wenn eine ANDERE Fahrt gerade übertragt - "Losfahren" muss dann warten. */
  otherRideTracking: boolean;
  /** Nur gesetzt, wenn `isTracking` - der letzte Fehler beim Hochladen. */
  trackingError: string | null;
  /** Beide leben in `App.tsx` - so läuft eine laufende Übertragung weiter,
   *  auch wenn man diesen Screen verlässt, um eine andere Fahrt anzusehen. */
  onBeginTracking: () => Promise<void>;
  onEndTracking: () => Promise<void>;
  onRide: (ride: Ride) => void;
  onFinished: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}

const POLL_INTERVAL_MS = 15_000;

export function DriverActive({
  session,
  ride,
  contact,
  onBack,
  isTracking,
  otherRideTracking,
  trackingError,
  onBeginTracking,
  onEndTracking,
  onRide,
  onFinished,
  onError,
  onNotice,
}: Props) {
  const [starting, setStarting] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [pushOnServer, setPushOnServer] = useState<boolean | null>(null);
  const [pushReady, setPushReady] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushHint, setPushHint] = useState<string | null>(null);

  // Nicht window.location.origin: in der Android-App waere das
  // https://localhost - ein Link, der beim Empfaenger ins Leere fuehrt.
  const joinUrl = joinLink(ride.code);

  // Zustand nachladen, damit der Fahrer sieht, wenn der Abholpunkt gesetzt wurde.
  useEffect(() => {
    const timer = window.setInterval(() => {
      api.get(session.rideId, session.token)
        .then(({ ride: fresh }) => onRide(fresh))
        .catch(() => { /* kurzer Aussetzer, beim nächsten Mal wieder */ });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [session, onRide]);

  // Ohne VAPID-Schluessel verschickt der Server ueberhaupt keine
  // Benachrichtigungen - das soll der Fahrer sehen, nicht erraten muessen.
  useEffect(() => {
    api.config()
      .then((config) => setPushOnServer(config.pushEnabled))
      .catch(() => setPushOnServer(null));
  }, []);

  // Der Fahrer bekommt sonst nie mit, wenn der Mitfahrer seinen Abholpunkt
  // ändert - ohne eigenes Push-Abo verpuffen die Meldungen des Servers
  // (sendToRole('driver', ...)) ins Leere. Ist die Erlaubnis schon erteilt,
  // reicht ein stilles Nachziehen; sonst zeigt die Karte unten den Knopf.
  useEffect(() => {
    if (pushSupport() !== 'granted') return;
    void enablePush(session.rideId, session.token).then((result) => {
      if (result.state === 'granted') setPushReady(true);
    });
    // Nur beim Betreten pruefen, nicht bei jeder Ride-Aktualisierung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function turnOnNotifications() {
    setPushBusy(true);
    try {
      const result = await enablePush(session.rideId, session.token);
      if (result.state === 'granted') {
        setPushReady(true);
        setPushHint(null);
        onNotice('Benachrichtigungen aktiv.');
      } else {
        setPushHint(result.message ?? 'Benachrichtigungen sind hier nicht verfügbar.');
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Benachrichtigungen fehlgeschlagen.');
    } finally {
      setPushBusy(false);
    }
  }

  // Die Standortübertragung lebt in `App.tsx` - so läuft sie weiter, wenn
  // man diesen Screen verlässt, um eine andere Fahrt anzusehen.
  async function begin() {
    setStarting(true);
    try {
      await onBeginTracking();
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Tracking konnte nicht gestartet werden.');
    } finally {
      setStarting(false);
    }
  }

  async function share() {
    const notice = await shareText({
      title: 'R2F - Abholung',
      text: `Ich hole dich ab! Tippe auf den Link, oder gib den Code ${ride.code} ein.`,
      url: joinUrl,
    });
    if (notice) onNotice(notice);
  }

  async function setPickup(coords: Coords) {
    try {
      const { ride: fresh } = await api.setPickup(session.rideId, session.token, coords);
      onRide(fresh);
    } catch (error) {
      onError(error instanceof Error ? error.message : 'Abholpunkt konnte nicht gesetzt werden.');
    }
  }

  async function end() {
    try {
      if (isTracking) await onEndTracking();
      await api.cancel(session.rideId, session.token);
    } catch {
      // Auch wenn der Server nicht erreichbar war: für den Fahrer ist Schluss.
    }
    onFinished();
  }

  function store(name: string) {
    saveRide({
      name,
      origin: ride.origin,
      pickup: ride.pickup,
      contactId: contact?.id ?? null,
      contactLabel: contact?.label ?? ride.passengerName ?? null,
    });
    onNotice('Fahrt gespeichert - beim nächsten Mal steht sie unter "Ich fahre".');
  }

  const estimate = ride.estimate;
  const transmitting = isPositionFresh(ride);

  return (
    <Page title="Deine Fahrt" onBack={onBack}>
      <Card>
        <div className="r2f-row r2f-row--between">
          <h2 style={{ margin: 0 }}>Mitfahrer einladen</h2>
          <IonBadge color={STATUS_COLOR[ride.status]}>{STATUS_LABEL[ride.status]}</IonBadge>
        </div>
        <div className="r2f-code">{ride.code}</div>
        <div className="r2f-row" style={{ gap: 8 }}>
          <IonButton expand="block" size="large" style={{ flex: 1 }} onClick={share}>
            <IonIcon slot="start" icon={shareOutline} />
            Link senden
          </IonButton>
          <IonButton
            fill="outline"
            size="large"
            onClick={() => {
              void navigator.clipboard.writeText(joinUrl);
              onNotice('Link kopiert.');
            }}
            aria-label="Link kopieren"
          >
            <IonIcon slot="icon-only" icon={copyOutline} />
          </IonButton>
        </div>
      </Card>

      {estimate && ride.pickup && (
        <Card>
          <div className="r2f-status">
            <div className="r2f-status__eta">{formatDuration(estimate.etaSeconds)}</div>
            <div className="r2f-status__label">
              noch {formatDistance(estimate.routeMeters)} bis {ride.passengerName}
            </div>
          </div>
        </Card>
      )}

      {isTracking && (
        <Card>
          <div className="r2f-row r2f-row--between">
            <h2 style={{ margin: 0 }}>Übertragung</h2>
            <span className={transmitting ? 'r2f-pill r2f-pill--live' : 'r2f-pill r2f-pill--stale'}>
              <span className={transmitting ? 'r2f-dot r2f-dot--pulsing' : 'r2f-dot'} />
              {ride.driver
                ? transmitting
                  ? 'Server hat deine Position'
                  : `zuletzt vor ${formatAge(ride.driver.ageSeconds)}`
                : 'noch nichts angekommen'}
            </span>
          </div>
          <p style={{ marginBottom: 0 }}>
            {ride.driver
              ? transmitting
                ? 'Deine Positionen kommen an. Der Mitfahrer wird automatisch benachrichtigt.'
                : 'Der Server hat länger nichts von dir gehört - ohne neue Position gibt es auch keine Benachrichtigung. Meist liegt das an der Akku-Optimierung für R2F oder an fehlendem Empfang.'
              : 'Noch ist keine Position beim Server angekommen. Das dauert nach dem Losfahren ein paar Sekunden.'}
          </p>
          {trackingError && (
            <div className="r2f-hint r2f-hint--warn" style={{ marginTop: 12 }}>
              Letzter Fehler beim Senden: {trackingError}
            </div>
          )}
        </Card>
      )}

      {pushOnServer === false && (
        <div className="r2f-hint r2f-hint--warn">
          Dieser Server verschickt keine Benachrichtigungen. Dein Mitfahrer sieht
          den Stand nur bei geöffneter App.
        </div>
      )}

      {pushOnServer && !pushReady && (
        <Card>
          <h2>Benachrichtigungen</h2>
          <p>Damit du mitbekommst, wenn dein Mitfahrer den Abholpunkt ändert.</p>
          <div style={{ marginTop: 12 }}>
            <IonButton expand="block" fill="outline" onClick={turnOnNotifications} disabled={pushBusy}>
              {pushBusy ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon slot="start" icon={notificationsOutline} />
                  Benachrichtigungen einschalten
                </>
              )}
            </IonButton>
          </div>
          {pushHint && <div className="r2f-hint" style={{ marginTop: 12 }}>{pushHint}</div>}
        </Card>
      )}

      {pushOnServer !== false && !ride.hasPassengerSubscription && (
        <div className="r2f-hint r2f-hint--warn">
          Dein Mitfahrer hat Benachrichtigungen noch nicht eingeschaltet - bis
          dahin erreicht ihn keine Meldung.
        </div>
      )}

      {isTracking ? (
        <IonButton expand="block" color="danger" fill="outline" onClick={() => setConfirmEnd(true)}>
          <IonIcon slot="start" icon={stopOutline} />
          Fahrt beenden
        </IonButton>
      ) : (
        <>
          <IonButton expand="block" size="large" onClick={begin} disabled={starting || otherRideTracking}>
            {starting ? (
              <IonSpinner name="dots" />
            ) : (
              <>
                <IonIcon slot="start" icon={playOutline} />
                Losfahren
              </>
            )}
          </IonButton>
          {otherRideTracking && (
            <p className="r2f-muted">
              Du überträgst gerade eine andere Fahrt - beende sie zuerst, um hier loszufahren.
            </p>
          )}
          <IonButton expand="block" fill="outline" color="danger" onClick={() => setConfirmEnd(true)}>
            Fahrt verwerfen
          </IonButton>
        </>
      )}

      {isTracking && !isNative() && (
        <div className="r2f-hint r2f-hint--warn">
          Im Browser stoppt die Standortverfolgung, sobald das Display ausgeht. Für eine Fahrt
          mit gesperrtem Handy nimm die Android-App.
        </div>
      )}

      <Card flush>
        <RideMap ride={ride} canNavigate />
      </Card>

      {!ride.pickup && (
        <Card>
          <h2>Abholpunkt fehlt noch</h2>
          <p>
            Dein Mitfahrer setzt ihn selbst, sobald er beitritt. Du kannst ihn aber auch schon
            hier auf der Karte festlegen.
          </p>
          <div style={{ marginTop: 12 }}>
            <MapPicker value={null} onChange={setPickup} kind="pickup" />
          </div>
        </Card>
      )}

      <IonButton expand="block" fill="outline" onClick={() => setSaveOpen(true)}>
        <IonIcon slot="start" icon={bookmarkOutline} />
        Diese Fahrt speichern
      </IonButton>

      {ride.timeline.length > 1 && (
        <Card>
          <h2>Verlauf</h2>
          <ul className="r2f-timeline">
            {[...ride.timeline].reverse().map((entry, index) => (
              <li key={`${entry.type}-${entry.at}-${index}`}>
                <time>{clockTime(entry.at)}</time>
                <span>
                  <strong>{timelineLabel(entry.type)}</strong>
                  {entry.text && <em>{entry.text}</em>}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <IonAlert
        isOpen={saveOpen}
        header="Fahrt speichern"
        message="Unter welchem Namen soll sie in der Schnellauswahl stehen? Startpunkt, Abholpunkt und Kontakt werden mitgesichert."
        inputs={[{ name: 'name', type: 'text', placeholder: 'z. B. Freitags zum Bahnhof', attributes: { maxlength: 40 } }]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          { text: 'Speichern', handler: (data: { name?: string }) => store(data.name ?? '') },
        ]}
        onDidDismiss={() => setSaveOpen(false)}
      />

      <IonAlert
        isOpen={confirmEnd}
        header={isTracking ? 'Fahrt beenden?' : 'Fahrt verwerfen?'}
        message={
          isTracking
            ? 'Die Standortverfolgung stoppt und dein Mitfahrer bekommt eine letzte Nachricht.'
            : 'Der Code wird ungültig. Für eine neue Fahrt legst du einfach eine neue an.'
        }
        buttons={[
          { text: isTracking ? 'Weiterfahren' : 'Behalten', role: 'cancel' },
          {
            text: isTracking ? 'Beenden' : 'Verwerfen',
            role: 'destructive',
            handler: () => { void end(); },
          },
        ]}
        onDidDismiss={() => setConfirmEnd(false)}
      />
    </Page>
  );
}
